
import React, { useState } from 'react';
import { AppRoute } from '../types';
import WaitlistSection from '../components/WaitlistSection';
import { WAITLIST_SCRIPT_URL } from '../constants';

interface HomeViewProps {
  onNavigate: (route: AppRoute) => void;
}

const HomeView: React.FC<HomeViewProps> = ({ onNavigate }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleHeroWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      // Attempt to send to Google Sheets. Using 'no-cors' as Google Apps Script redirects
      // are often restricted by standard CORS policies, but the data still transmits.
      await fetch(WAITLIST_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'Hero Section' }),
      });
      
      setIsSuccess(true);
      setEmail('');
    } catch (error) {
      console.error('Waitlist submission failed:', error);
      // Fallback for visual continuity if there's a network error
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-grid-pattern overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative border-b border-border overflow-hidden bg-background">
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="text-center pt-16 md:pt-24 pb-20 px-6 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-white text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-600 mb-8 shadow-sm">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
              Local Manufacturing Network
            </div>
            
            <h1 className="font-display text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[5rem] tracking-tighter text-primary font-bold uppercase leading-[0.95] mb-8 select-none">
              UPLOAD. PRINT.<br/>
              <span className="text-neutral-300">ENJOY.</span>
            </h1>

            {/* Interactive Flow Diagram */}
            <div className="flex flex-col md:flex-row w-full max-w-4xl mx-auto relative items-center justify-between gap-12 md:gap-0 px-4 md:px-20 py-12 my-8">
              <div className="hidden md:block absolute inset-0 pointer-events-none">
                <svg className="w-full h-full" fill="none" preserveAspectRatio="none">
                  <path d="M120,84 L400,84" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" className="text-neutral-200" />
                  <path d="M500,84 L780,84" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" className="text-neutral-200" />
                  <circle r="2.5" fill="#111111">
                    <animateMotion dur="3s" repeatCount="indefinite" path="M120,84 L400,84" />
                  </circle>
                  <circle r="2.5" fill="#111111">
                    <animateMotion dur="3s" repeatCount="indefinite" path="M500,84 L780,84" />
                  </circle>
                </svg>
              </div>

              <div className="flex flex-col items-center gap-3 relative z-20">
                <div className="w-12 h-12 md:w-4 md:h-4 rounded-full bg-neutral-200 border border-neutral-300 flex items-center justify-center">
                   <iconify-icon icon="lucide:users" class="md:hidden text-neutral-500 text-xl"></iconify-icon>
                   <div className="hidden md:block absolute inset-0 rounded-full bg-primary/10 animate-ping"></div>
                </div>
                <span className="text-[10px] md:text-[9px] font-mono uppercase tracking-[0.3em] text-neutral-500 font-bold">Users</span>
              </div>

              <div className="relative z-20 group">
                <div className="flex flex-col items-center gap-4 md:gap-6">
                  <div className="w-24 h-24 md:w-32 md:h-32 bg-primary rounded-full flex items-center justify-center shadow-2xl relative transition-all duration-700 group-hover:scale-105 group-hover:bg-neutral-800">
                    <iconify-icon icon="pixelarticons:printer" width="32" height="32" class="md:hidden text-white"></iconify-icon>
                    <iconify-icon icon="pixelarticons:printer" width="48" height="48" class="hidden md:block text-white"></iconify-icon>
                    <div className="absolute inset-0 rounded-full border border-primary/20 animate-ping"></div>
                  </div>
                  <span className="text-[11px] md:text-[10px] font-mono uppercase tracking-[0.4em] text-primary font-bold">Smart Printer</span>
                </div>
              </div>

              <div className="flex flex-col items-center gap-3 relative z-20">
                <div className="w-12 h-12 md:w-4 md:h-4 rounded-full bg-neutral-200 border border-neutral-300 flex items-center justify-center">
                   <iconify-icon icon="lucide:package" class="md:hidden text-neutral-500 text-xl"></iconify-icon>
                </div>
                <span className="text-[10px] md:text-[9px] font-mono uppercase tracking-[0.3em] text-neutral-500 font-bold">Deliver</span>
              </div>
            </div>

            <p className="text-lg md:text-2xl text-neutral-600 leading-relaxed font-normal max-w-2xl mx-auto mb-14 text-balance px-4">
              Neighborhood printers turned into a <span className="text-primary font-semibold">secure global factory</span>. Zero shipping, infinite scale.
            </p>

            <div className="max-w-md mx-auto px-4">
              {!isSuccess ? (
                <form onSubmit={handleHeroWaitlist} className="flex flex-col sm:flex-row gap-3 p-2 bg-white border border-border rounded-3xl sm:rounded-full shadow-lg transition-all focus-within:border-primary">
                  <input 
                    type="email" 
                    placeholder="Enter your email" 
                    className="flex-grow px-6 py-4 bg-transparent text-sm focus:outline-none placeholder:text-neutral-400 font-medium"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary text-white h-14 px-8 rounded-2xl sm:rounded-full text-[11px] font-bold uppercase tracking-widest hover:bg-neutral-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <iconify-icon icon="lucide:loader-2" class="animate-spin text-lg"></iconify-icon>
                    ) : (
                      <>Join Waitlist <iconify-icon icon="lucide:arrow-right" width="16"></iconify-icon></>
                    )}
                  </button>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-3 py-6 px-12 bg-white border border-border rounded-full animate-in fade-in zoom-in duration-500">
                  <iconify-icon icon="lucide:check-circle" class="text-green-500 text-xl"></iconify-icon>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-primary">Added to early access</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Grid Sections */}
      <section className="max-w-[1400px] mx-auto border-x border-border border-b bg-white py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-24">
          <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.2em] mb-4 block">The Challenge</span>
          <p className="text-neutral-600 text-lg md:text-xl font-normal max-w-xl mx-auto">Replacing global supply chains with local community nodes.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {[
            { t: 'High Barrier', d: 'Industrial quality printing requires space, power, and capital. Most creators are priced out.', i: 'lucide:lock' },
            { t: 'Logistical Waste', d: 'Shipping across oceans creates 10x more CO2 than the production itself.', i: 'lucide:truck' },
            { t: 'Network Void', d: 'Your neighbor likely has an idle printer, but there is no secure bridge to connect you.', i: 'lucide:user-minus' }
          ].map((item, idx) => (
            <div key={idx} className="space-y-6 group p-6 rounded-3xl hover:bg-neutral-50 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center border border-border group-hover:bg-primary group-hover:text-white transition-all">
                <iconify-icon icon={item.i} class="text-2xl"></iconify-icon>
              </div>
              <h3 className="font-bold text-primary uppercase text-sm tracking-[0.1em]">{item.t}</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">{item.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-[1400px] mx-auto border-x border-border border-b bg-primary text-white py-16 px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
          {[
            { v: '48h', l: 'Avg Delivery' },
            { v: '250+', l: 'Verified Nodes' },
            { v: '85%', l: 'Less Carbon' },
            { v: '12k', l: 'Prints Done' }
          ].map((stat, i) => (
            <div key={i}>
              <div className="text-3xl md:text-5xl font-display font-bold mb-2">{stat.v}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{stat.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Protocol Grid */}
      <section className="max-w-[1400px] mx-auto border-x border-border border-b bg-white py-20 md:py-32 px-6">
        <div className="text-center mb-16 md:mb-24">
          <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.2em] mb-4 block">Operation</span>
          <h2 className="text-4xl md:text-5xl font-display uppercase tracking-tighter">The Zetra Protocol</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { n: '01', t: 'Design Sync', d: 'Upload your CAD. Our AI verifies geometry for FDM/SLA compatibility.' },
            { n: '02', t: 'Node Auction', d: 'Your job is broadcasted to the nearest 5 nodes for secure bidding.' },
            { n: '03', t: 'Local Forge', d: 'The winning node begins printing. Track progress via live telemetry.' },
            { n: '04', t: 'Peer Delivery', d: 'Secured pickup or bike courier delivery to your exact location.' }
          ].map((step, idx) => (
            <div key={idx} className="relative p-10 bg-neutral-50 rounded-[2.5rem] hover:bg-white border border-transparent hover:border-border transition-all group overflow-hidden h-full">
              <span className="text-6xl md:text-7xl font-display font-bold text-neutral-200/40 absolute -right-4 -bottom-4 group-hover:text-primary/5 transition-colors">{step.n}</span>
              <h3 className="text-xs font-bold text-primary mb-4 uppercase tracking-[0.2em] relative z-10">{step.t}</h3>
              <p className="text-xs text-neutral-600 leading-relaxed relative z-10">{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      <WaitlistSection />
    </div>
  );
};

export default HomeView;
